import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import { DATADOG_ACTIONS } from '@cartwhl/tracking/datadogConstants';
import Loading from '@cartwhl/ui/atoms/Loading';
import Slot from '@cartwhl/ui/atoms/Slot';
import Text from '@cartwhl/ui/base/Text';
import { FileUploader } from '@cartwhl/ui/feature/FileUploader';
import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import CheckedCircleIcon from '@cartwhl/ui/icons/CheckedCircleIcon';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import CircleArrow from '@cartwhl/ui/icons/CircleArrow';
import MagnifyIcon from '@cartwhl/ui/icons/MagnifyIcon';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';
import SubPanel from '@cartwhl/ui/layout/SubPanel';
import GenerationButton from '@cartwhl/ui/molecules/GenerationButton';

import { characterPickerStore, noticesStore } from 'stores';
import { uiStore } from 'stores';
import { resetCharacterPickerState } from 'stores/characterPickerStore';

import { CHAR_GEN_DESKTOP_MIN_WIDTH } from '../constants';
import { MagnifiedImageView } from './MagnifiedImageView';

const OFFSET_TOP = 132;
const OFFSET_BOTTOM = 20;
interface GenerateCharacterSubpanelProps {
  id: string;
  elementToTrack: HTMLButtonElement | null;
}
const FixedHeightTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>((props, ref) => {
  const [localValue, setLocalValue] = useState(props.value);
  const internalRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => internalRef.current!);
  useEffect(() => {
    setLocalValue(props.value);
  }, [props.value]);
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    props.onChange && props.onChange(e);
  };
  return (
    <textarea
      {...props}
      ref={internalRef}
      value={localValue}
      onChange={handleChange}
      style={{
        resize: 'none',
        overflowY: 'auto',
        boxSizing: 'border-box',
        ...props.style,
      }}
      className={`bg-gray-15 dark:bg-gray-75 placeholder:text-gray-45 border-gray-40 w-full min-w-0 rounded-lg border px-4 py-2 pb-20 text-[14px]/[18px] text-sm font-normal outline-hidden placeholder:text-sm dark:text-white ${props.className || ''}`}
    />
  );
});

const ACCEPTED_IMAGE_TYPES = ['.png', '.jpg', '.jpeg'];
const MAX_IMAGE_SIZE = 7 * 1024 * 1024; // 7MB in bytes

function ImageReferenceUploader() {
  const { referenceUpload } = useSnapshot(characterPickerStore.state);

  return (
    <FileUploader
      fileContent="Image Reference"
      accept={{
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
      }}
      maxFileSize={MAX_IMAGE_SIZE}
      upload={referenceUpload}
      onDrop={(file) => characterPickerStore.uploadImageReference(file)}
      onRemove={() => characterPickerStore.removeUpload()}
      onCancel={(uploadId: string) =>
        characterPickerStore.deleteCustomCharacter(uploadId, true)
      }
      cancelable
    />
  );
}

export default function GenerateCharacterSubpanel({
  id,
  elementToTrack,
}: GenerateCharacterSubpanelProps) {
  const { subPanelKey } = useSnapshot(uiStore.state);
  const {
    generationInProgress,
    generationJobID,
    imageGenerationImages,
    imageGenerationError,
    imageGenerationProgress,
    loading,
    magnifiedImageIndex,
    promptText,
    selectedImageIndex,
    spinnerText,
    upload,
  } = useSnapshot(characterPickerStore.state);

  const isMobile = !useMediaQuery(
    `(min-width: ${CHAR_GEN_DESKTOP_MIN_WIDTH}px)`,
  );

  const [yPosition, setYPosition] = useState<number | undefined>(undefined);
  const animationFrameRef = useRef<number | null>(null);
  const subpanelRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'large' | 'small'>('large');
  const imagesLoaded = imageGenerationImages.length > 0;

  useEffect(() => {
    function checkLayout() {
      const availW = window.innerWidth;
      const availH = window.innerHeight - OFFSET_TOP - OFFSET_BOTTOM;
      const nonImageUI = 280; // header + footer + padding
      const neededH_large = 512 + nonImageUI;
      const neededW_large = 1200;
      if (availW >= neededW_large && availH >= neededH_large) {
        setLayoutMode('large');
      } else {
        setLayoutMode('small');
      }
    }
    window.addEventListener('resize', checkLayout);
    checkLayout();
    return () => window.removeEventListener('resize', checkLayout);
  }, []);

  useEffect(() => {
    if (!textContainerRef.current) return;
    const textarea = textContainerRef.current.querySelector('textarea');
    if (!textarea) return;
    const observer = new ResizeObserver(() => {
      setHasScrollbar(textarea.scrollHeight > textarea.clientHeight);
    });
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [promptText]);
  const updateYPosition = () => {
    if (elementToTrack) {
      const rect = elementToTrack.getBoundingClientRect();
      const trackY = Math.max(rect.top, OFFSET_TOP);
      const heightContent = subpanelRef.current?.offsetHeight;
      const heightAvailable = window.innerHeight - OFFSET_BOTTOM;
      if (!heightContent) return;
      const overflow = heightContent + trackY - heightAvailable;
      let y = trackY;
      if (overflow > 0) {
        y = trackY - overflow;
      }
      setYPosition(Math.max(y, OFFSET_TOP));
    }
  };

  useEffect(() => {
    if (subPanelKey !== id) return;
    updateYPosition();
    const mainPanelScrollArea = document.getElementById(
      'main-panel-scroll-area',
    );
    if (subPanelKey === id) {
      mainPanelScrollArea?.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', updateYPosition);
    }
    return () => {
      mainPanelScrollArea?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateYPosition);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [id, subPanelKey]);
  const handleScroll = () => {
    uiStore.setSubPanelKey('');
  };
  useClickOutside(subpanelRef, () => uiStore.setSubPanelKey(''), {
    exceptionsRefs: [{ current: elementToTrack as HTMLElement }],
    shouldTrigger: (e) => {
      const target = e.target as HTMLElement;
      return !target.closest('[data-state]');
    },
  });
  if (subPanelKey !== id) return null;
  const handleGenerateClick = () => {
    if (promptText.length === 0) {
      noticesStore.error('Please enter a prompt to generate an image.');
      return;
    }
    characterPickerStore.generateImagesFromPrompt(promptText);
  };
  const handleContinueClick = async () => {
    if (
      !generationJobID ||
      typeof selectedImageIndex !== 'number' ||
      !imageGenerationImages[selectedImageIndex]
    ) {
      characterPickerStore.setImageGenerationError(
        'No image selected or invalid job data.',
      );
      return;
    }
    // example of imageGenerationImages: "https://cartwheel-mogen-resources-staging.s3.us-east-1.amazonaws.com/character-generations/user-c3b487b8-d4e9-41e3-9fd1-ccac662707c1/char-generation-01JVQB0K8HF06F3PSWBA28T0XF/imagen_file_0.png?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Credential=ASIAXVNUOZ4V4C2WDIRD%2F20250520%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20250520T171422Z&X-Amz-Expires=43200&X-Amz-Security-Token=IQoJb3JpZ2luX2VjEPL%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEaCXVzLWVhc3QtMSJIMEYCIQC%2FA%2BK3FQ8J5cvDZIn2jwGd6qIxRNvcnQpEPvgmqjt65gIhAI%2BsrxMPTnJKpn6EUYdPabJQFt1JDy0Di9LuYquRgPLgKqQDCKr%2F%2F%2F%2F%2F%2F%2F%2F%2F%2FwEQARoMNTI3MDQ4ODIyNTcxIgxp42xW%2F01sQqqP1Zwq%2BAIr9ya%2BRPhvCGZ21N08s2WKoVIw%2Bpx3WrLT3Ccs9qbx3WC%2B3WPuUsgwHMKBUx2WCcjO0M%2BSmyqDK3%2BnBwNfCk9yIr39ZelPqz8Rgd9j1Jd06xPp%2Bf070CV0tw9YnqksAktp8RqProREEWeg3dZX7DYCJl92FJGD7fRlnqs6owAZcAVsCDRW259J8ssMzFFFMkHCrEqfa%2BpuIRA9ure3tXu%2BtLJAU%2FxzNoVcBL9Lk0VvGgyP6uIRuAVTFRISgOfFI6Y0F5fjOpAHZdd%2F%2BxZhb0%2FU%2FzlVOkkY1l%2FO60rYgiK1znRIR5sH4yoDU2vXsVne1jszVv9k3aAH0nqin5EgEu7wPK5%2FzRck3yckS3swqpks4p5J5bKuOiRftpISXhdZj9Xe2nb1ov%2FKw9uAXY2tLaCAjki7b9Tt2u6SNdiknLefrsXHgvXdhnezk7nxVAFfG5qJ%2BJobO3hzksYQxMnAFWycYycqlIRYSUgDOsbflDibMF1NBwQRW3QmMLvussEGOpwBGm2bo5umbwkIO3%2FzqPscognU7uvye1KiPxJDGtny70fNVqgJsQIh75Ap9%2FQ83A8V2VH35REHaAYbj%2BCaYWFGVMWqFgejmZZsTSaIasKeEw%2Fmp%2F8UcIgs7SOU9805c4shZs7MRqM%2BPtsbHDwAUX3wLDhYbcXf7A67XP9V17PJtc05thJDH1af4UUnBqgz%2FXekTiD1qK%2BV2Olzuq7q&X-Amz-Signature=394742877a0e6274d5d045c71dbeae91f2e8317823fa7550aaefe97a0c82de5b&X-Amz-SignedHeaders=host&response-content-disposition=attachment%3B%20filename%3D%22generated-image-0.png%22&x-amz-checksum-mode=ENABLED&x-id=GetObject
    let fileExtension = 'png';
    try {
      const url = new URL(imageGenerationImages[selectedImageIndex] || '');
      const pathname = url.pathname;
      fileExtension = pathname.split('.').pop()?.toLowerCase() || 'png';
    } catch (error) {
      console.error(
        'Error parsing image URL, keeping default of',
        fileExtension,
      );
    }

    characterPickerStore.createCharacterFromImage({
      fileExtension,
      imageIndex: selectedImageIndex,
      jobID: generationJobID,
      prompt: promptText,
    });
  };

  const isRegen = imagesLoaded || loading.imageGeneration;
  const disabled = loading.imageGeneration;
  const baseClass = `absolute bottom-3 ${hasScrollbar ? 'right-7' : 'right-3'}`;

  const placeholderText =
    'Describe your character here...\n\nNote: Stylized characters or cartoon characters work the best for now. More realistic characters are still a challenge. Stay tuned for future updates!';

  // Mobile UI, I had to rearrange the layout of most things - Let me know if this should be in a separate component that is called right here
  if (isMobile) {
    return (
      <SubPanel
        id={id}
        elementToTrack={elementToTrack}
        closePanel={() => uiStore.setSubPanelKey('')}
        subPanelKey={subPanelKey}
      >
        <div
          ref={subpanelRef}
          className="dark:bg-gray-90 flex flex-col bg-white"
          style={{ maxHeight: 'calc(100vh - 60px)' }}
        >
          {/* Outer container */}
          <div
            ref={subpanelRef}
            className="dark:bg-gray-90 flex flex-col gap-4 bg-white"
          >
            {/* Header and subtitle */}
            <div className="space-y-1">
              <div className="flex flex-col">
                <Text variant="studio/title/md" className="text-lg">
                  Generate new character
                </Text>
                <Text
                  variant="studio/body/medium/md"
                  className="text-gray-45 pt-4 text-sm dark:text-white"
                >
                  {imagesLoaded ? 'Step 2 of 2' : 'Step 1 of 2'}
                </Text>
              </div>
              <Text
                variant="studio/body/sm"
                className="text-black dark:text-white"
              >
                Generate a custom character via text prompt.
              </Text>
            </div>

            {/* Top Section: Image Display/Upload */}
            <div
              className={`w-full overflow-x-auto overflow-y-visible ${
                magnifiedImageIndex === null
                  ? 'h-[264px]'
                  : 'ring-blue aspect-square overflow-hidden rounded-lg ring-4'
              } `}
            >
              {imageGenerationError ? (
                <div className="border-gray-30 dark:border-gray-60 dark:bg-gray-90 flex h-full w-full items-center justify-center overflow-hidden rounded-lg border bg-white p-4">
                  <div className="text-center">
                    <WarningIcon className="text-red mx-auto mb-2 h-6 w-6" />
                    <Text
                      as="div"
                      variant="studio/body/sm"
                      className="text-red font-medium"
                    >
                      Uh oh! We're unable to generate that for you.
                    </Text>
                    <Text
                      as="div"
                      variant="studio/body/sm"
                      className="text-gray-60 dark:text-gray-30"
                    >
                      If you continually run into this issue try updating the
                      <br />
                      prompt in accordance to our generation guidelines.
                    </Text>
                    <Button variant="secondary" size="sm" className="mt-4">
                      <Link
                        href="https://docs.getcartwheel.com/docs/custom-characters/character-generation#generation-guidelines"
                        target="_blank"
                        rel="noreferrer"
                      >
                        View guidelines
                      </Link>
                    </Button>
                  </div>
                </div>
              ) : imageGenerationImages.length === 0 ? (
                <div
                  className={`text-gray-60 flex h-full w-full items-center justify-center rounded-lg ${
                    loading.imageGeneration &&
                    'border-gray-30 dark:bg-gray-90 dark:border-gray-60 border bg-white'
                  }`}
                >
                  {loading.imageGeneration ? (
                    <GenerationButton
                      progress={{
                        percentage: imageGenerationProgress,
                        statusText: `Generating concepts... ${Math.round(
                          imageGenerationProgress,
                        )}%`,
                      }}
                      disabled={true}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <ImageReferenceUploader />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {magnifiedImageIndex === null ? (
                    <>
                      <div className="flex h-[264px] w-full items-center gap-3 overflow-x-auto overflow-y-visible pr-1 pl-1">
                        {imageGenerationImages.map((url, idx) => (
                          <div
                            key={idx}
                            onClick={() =>
                              characterPickerStore.setSelectedImageIndex(idx)
                            }
                            className="group relative aspect-square h-[256px] shrink-0 overflow-visible"
                          >
                            {/* clip the image and round its corners */}
                            <div
                              className={`overflow-hidden rounded-lg ${
                                selectedImageIndex === idx
                                  ? 'border-blue border-4'
                                  : 'border-gray-15 border-4'
                              }`}
                            >
                              <img
                                src={url}
                                className="h-full w-full object-cover"
                                crossOrigin="use-credentials"
                              />
                            </div>
                            {selectedImageIndex === idx && (
                              <div className="absolute top-3 left-3">
                                <CheckedCircleIcon className="bg-blue h-6 w-6 rounded-full text-white" />
                              </div>
                            )}
                            <div className="absolute top-3 right-3 opacity-0 transition-opacity group-hover:opacity-100">
                              <button
                                type="button"
                                aria-label="Magnify image"
                                className="bg-gray-200 dark:bg-gray-75 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg p-0 text-gray-90 dark:text-white hover:brightness-90 dark:hover:brightness-110"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  characterPickerStore.setMagnifiedImageIndex(
                                    idx,
                                  );
                                  characterPickerStore.setSelectedImageIndex(
                                    idx,
                                  );
                                }}
                              >
                                <MagnifyIcon className="h-[22px] w-[22px]" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="relative flex w-full items-center justify-center bg-black">
                      <img
                        src={imageGenerationImages[magnifiedImageIndex]!}
                        className="h-full w-full object-contain"
                        crossOrigin="use-credentials"
                      />
                      {!generationInProgress && (
                        <div className="absolute top-3 left-3">
                          <button
                            type="button"
                            aria-label="Back"
                            className="bg-gray-200 dark:bg-gray-75 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg p-0 text-gray-90 dark:text-white hover:brightness-90 dark:hover:brightness-110"
                            onClick={() =>
                              characterPickerStore.setMagnifiedImageIndex(null)
                            }
                          >
                            <ChevronLeftIcon className="h-[22px] w-[22px]" />
                          </button>
                        </div>
                      )}
                      {generationInProgress && (
                        <>
                          <div className="pointer-events-none absolute inset-0 bg-gray-200/50" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="dark:text-gray-90 rounded-2xl bg-white p-6 text-center">
                              <Loading
                                className="text-green mx-auto mb-3 h-6 w-6"
                                trackClassName="stroke-gray-30 dark:stroke-gray-60"
                              />
                              <Text variant="studio/body/sm">
                                {spinnerText}{' '}
                                <Percentage
                                  value={upload?.processingPercentage}
                                />
                              </Text>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Text input area */}
            {magnifiedImageIndex === null && (
              <div className="flex-1">
                <div
                  ref={textContainerRef}
                  className="relative flex h-[192px] min-h-0 flex-col"
                >
                  <FixedHeightTextarea
                    placeholder={placeholderText}
                    value={promptText}
                    onChange={(e) =>
                      characterPickerStore.setPromptText(e.target.value)
                    }
                    disabled={loading.imageGeneration || generationInProgress}
                    style={{ flex: '1 1 0%', minHeight: 0 }}
                    className={`bg-gray-15 dark:bg-gray-75 placeholder:text-gray-45 border-gray-30 dark:border-gray-60 min-h-0 min-w-0 w-full flex-1 rounded-lg border px-4 py-2 pb-20 outline-hidden ${
                      loading.imageGeneration || generationInProgress
                        ? 'text-gray-60 dark:text-gray-45 cursor-not-allowed'
                        : 'dark:text-white'
                    } `}
                  />
                  {/* Text box gradient for scrolling */}
                  <>
                    {/* light-mode (gray-15) */}
                    <div
                      className="pointer-events-none absolute right-4 bottom-px left-4 block dark:hidden"
                      style={{
                        height: '120px',
                        backgroundImage:
                          'linear-gradient(to top, #F6F6F6 0px, #F6F6F6 45px, rgba(246,246,246,0.8) 55px, rgba(246,246,246,0.5) 67px, transparent 80px)',
                      }}
                    />
                    {/* dark-mode (gray-75) */}
                    <div
                      className="pointer-events-none absolute right-4 bottom-px left-4 hidden dark:block"
                      style={{
                        height: '120px',
                        backgroundImage:
                          'linear-gradient(to top, #3D3D3D 0px, #3D3D3D 45px, rgba(61,61,61,0.8) 55px, rgba(61,61,61,0.5) 67px, transparent 80px)',
                      }}
                    />
                  </>
                  {!generationInProgress && (
                    <>
                      {/* Generate / Re-generate */}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleGenerateClick}
                        className={baseClass}
                        icon={isRegen ? <CircleArrow /> : undefined}
                      >
                        {isRegen ? 'Re-generate' : 'Generate'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Bottom section with two equal-width buttons */}
            <div>
              {!generationInProgress ? (
                <div className="flex w-full gap-4">
                  <Button
                    variant="secondary"
                    disabled={!imagesLoaded && !imageGenerationError}
                    className="w-1/2"
                    onClick={() => {
                      resetCharacterPickerState();
                    }}
                  >
                    Start over
                  </Button>
                  <Button
                    className="w-1/2"
                    variant={imagesLoaded ? 'primary' : 'secondary'}
                    disabled={!imagesLoaded}
                    onClick={handleContinueClick}
                    data-dd-action-name={DATADOG_ACTIONS.GENERATE_CHARACTER}
                  >
                    Continue
                  </Button>
                </div>
              ) : (
                <div className="pt-5">
                  <Button
                    className="w-full"
                    size="sm"
                    variant="secondary"
                    disabled
                  >
                    Continue
                  </Button>
                </div>
              )}
            </div>
            <Text variant="studio/body/medium/sm" className="text-blue">
              <Link
                href="https://docs.getcartwheel.com/docs/custom-characters/character-generation#generation-guidelines"
                target="_blank"
              >
                Generation guidelines
              </Link>
            </Text>
          </div>
        </div>
      </SubPanel>
    );
  }

  return (
    <Slot elementId="sub-panel">
      <div
        className={`dark:bg-gray-90 absolute z-20 rounded-2xl bg-white transition-[transform,opacity] ${
          yPosition === undefined
            ? 'pointer-events-none translate-x-10 opacity-0'
            : 'pointer-events-auto translate-x-0 opacity-100'
        } ${
          layoutMode === 'small'
            ? 'right-[calc(100%)]'
            : 'lg:right-[calc(100%)]'
        } `}
        style={{
          top: layoutMode === 'large' ? `${(yPosition ?? 60) - 100}px` : '80px',
          width: layoutMode === 'large' ? '873px' : '650px',
        }}
      >
        {/* Outer container */}
        <div
          ref={subpanelRef}
          className="dark:bg-gray-90 no-scrollbar lg:shadow-menu lg:dark:border-gray-75 flex flex-col gap-4 overflow-y-scroll rounded-2xl bg-white p-6 lg:dark:border-2"
        >
          {/* Header and subtitle */}
          <div className="space-y-1">
            <div className="mb-1 flex items-center justify-between">
              <Text variant="studio/title/md" className="text-lg">
                Generate new character
              </Text>
              <Text variant="studio/body/medium/sm">
                {imagesLoaded ? 'Step 2 of 2' : 'Step 1 of 2'}
              </Text>
            </div>
            <Text
              variant="studio/body/sm"
              className="text-gray-60 dark:text-white"
            >
              Generate a custom character via text prompt.
            </Text>
          </div>
          {/* Top divider */}
          <hr className="border-gray-30 dark:border-gray-60 -mx-6 my-0 border-t" />
          {/* Two-column layout for text input and image upload */}
          <div className="flex items-start gap-4">
            {/* Left Column: Text Input */}
            <div className="flex min-w-0 flex-1 flex-col">
              <Text as="div" variant="studio/body/sm" className="mb-2">
                {loading.imageGeneration || imagesLoaded
                  ? 'Edit your prompt if necessary:'
                  : 'Enter your prompt.'}
              </Text>
              {/* Text input area */}
              <div
                ref={textContainerRef}
                className="relative flex min-h-0 flex-col"
                style={{
                  height: layoutMode === 'large' ? '512px' : '256px',
                }}
              >
                <FixedHeightTextarea
                  placeholder={placeholderText}
                  value={promptText}
                  onChange={(e) =>
                    characterPickerStore.setPromptText(e.target.value)
                  }
                  disabled={loading.imageGeneration || generationInProgress}
                  style={{ flex: '1 1 0%', minHeight: 0 }}
                  className={`bg-gray-15 dark:bg-gray-75 placeholder:text-gray-45 border-gray-30 dark:border-gray-60 min-h-0 min-w-0 w-full flex-1 rounded-lg border px-4 py-2 pb-20 text-[20px]/[24px] outline-hidden ${
                    loading.imageGeneration || generationInProgress
                      ? 'text-gray-60 dark:text-gray-45 cursor-not-allowed'
                      : 'dark:text-white'
                  } `}
                />
                {/* Text box bottom gradient for scrolling */}
                <>
                  {/* light-mode (gray-15) */}
                  <div
                    className="pointer-events-none absolute right-4 bottom-px left-4 block dark:hidden"
                    style={{
                      height: '100px',
                      backgroundImage:
                        'linear-gradient(to top, #F6F6F6 0px, #F6F6F6 60px, rgba(246,246,246,0.8) 75px, rgba(246,246,246,0.5) 82px, transparent 90px)',
                    }}
                  />
                  {/* dark-mode (gray-75) */}
                  <div
                    className="pointer-events-none absolute right-4 bottom-px left-4 hidden dark:block"
                    style={{
                      height: '100px',
                      backgroundImage:
                        'linear-gradient(to top, #3D3D3D 0px, #3D3D3D 60px, rgba(61,61,61,0.8) 75px, rgba(61,61,61,0.5) 82px, transparent 90px)',
                    }}
                  />
                </>
                {/* Hide generation buttons when generation is in progress */}
                {!generationInProgress && (
                  <>
                    {/* Generate / Re-generate */}
                    <Button
                      variant={isRegen ? 'secondary' : 'primary'}
                      size="sm"
                      disabled={disabled}
                      onClick={handleGenerateClick}
                      className={baseClass}
                      icon={isRegen ? <CircleArrow /> : undefined}
                    >
                      {isRegen ? 'Re-generate' : 'Generate'}
                    </Button>
                  </>
                )}
              </div>
              {/* Generation guidelines link */}
              <div className="pt-2">
                <Text variant="studio/body/medium/sm" className="text-blue">
                  <Link
                    href="https://docs.getcartwheel.com/docs/custom-characters/character-generation#generation-guidelines"
                    target="_blank"
                  >
                    Generation guidelines
                  </Link>
                </Text>
              </div>
            </div>
            {/* Right Column: Image Upload — height lives on inner stage only (same as prompt box), not on wrapper + label */}
            <div
              className="flex shrink-0 flex-col"
              style={{
                width: layoutMode === 'large' ? '512px' : '256px',
              }}
            >
              <Text as="div" variant="studio/body/sm" className="mb-2">
                {loading.imageGeneration || imagesLoaded
                  ? 'Select an option:'
                  : 'Or upload an image as reference.'}
              </Text>
              <div
                className="relative min-h-0 w-full overflow-hidden"
                style={{
                  height: layoutMode === 'large' ? '512px' : '256px',
                }}
              >
                {imageGenerationError ? (
                  <div className="border-gray-30 dark:border-gray-60 dark:bg-gray-90 flex h-full w-full items-center justify-center overflow-hidden rounded-lg border bg-white p-4">
                    <div className="text-center">
                      <WarningIcon className="text-red mx-auto mb-2 h-6 w-6" />
                      <Text
                        as="div"
                        variant="studio/body/sm"
                        className="text-red font-medium"
                      >
                        Uh oh! We're unable to generate that for you.
                      </Text>
                      <Text
                        as="div"
                        variant="studio/body/sm"
                        className="text-gray-60 dark:text-gray-30"
                      >
                        If you continually run into this issue try updating the
                        <br />
                        prompt in accordance to our generation guidelines.
                      </Text>
                      <Button variant="secondary" size="sm" className="mt-4">
                        View guidelines
                      </Button>
                    </div>
                  </div>
                ) : imageGenerationImages.length === 0 ? (
                  <div
                    className={`text-gray-60 flex h-full w-full items-center justify-center rounded-lg ${
                      loading.imageGeneration &&
                      'border-gray-30 dark:bg-gray-90 dark:border-gray-60 border bg-white'
                    }`}
                  >
                    {loading.imageGeneration ? (
                      <GenerationButton
                        progress={{
                          percentage: imageGenerationProgress,
                          statusText: `Generating images... ${Math.round(
                            imageGenerationProgress,
                          )}%`,
                        }}
                        disabled={true}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <ImageReferenceUploader />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {magnifiedImageIndex === null ? (
                      <div className="grid h-full w-full grid-cols-2 gap-3 overflow-visible">
                        {imageGenerationImages.map((url, idx) => (
                          <div
                            key={idx}
                            onClick={() =>
                              characterPickerStore.setSelectedImageIndex(idx)
                            }
                            className={`group relative aspect-square w-full overflow-hidden rounded-lg ${
                              selectedImageIndex === idx
                                ? 'border-blue border-4'
                                : 'border-gray-30 border-4'
                            }`}
                          >
                            <img
                              src={url}
                              className="absolute inset-0 h-full w-full object-cover"
                              crossOrigin="use-credentials"
                            />
                            {/* Checked icon in top left if selected */}
                            {selectedImageIndex === idx && (
                              <div className="absolute top-3 left-3">
                                <CheckedCircleIcon className="bg-blue h-6 w-6 rounded-full text-white" />
                              </div>
                            )}
                            <div className="absolute top-3 right-3 opacity-0 transition-opacity group-hover:opacity-100">
                              <button
                                type="button"
                                aria-label="Magnify image"
                                className="bg-gray-200 dark:bg-gray-75 flex h-7 w-7 cursor-pointer items-center justify-center rounded-lg p-0 text-gray-90 dark:text-white hover:brightness-90 dark:hover:brightness-110"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  characterPickerStore.setMagnifiedImageIndex(
                                    idx,
                                  );
                                  characterPickerStore.setSelectedImageIndex(
                                    idx,
                                  );
                                }}
                              >
                                <MagnifyIcon className="h-[22px] w-[22px]" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <MagnifiedImageView
                        imageURL={imageGenerationImages[magnifiedImageIndex]}
                        spinnerText={spinnerText}
                        generationInProgress={generationInProgress}
                        processingPercentage={upload?.processingPercentage}
                        onClose={() =>
                          characterPickerStore.setMagnifiedImageIndex(null)
                        }
                      />
                    )}
                  </>
                )}
              </div>
              {/* Generation History link  would go here*/}
            </div>
          </div>
          {/* Bottom section with divider and button(s) */}
          <div className="flex flex-col gap-0">
            <hr className="border-gray-30 dark:border-gray-60 -mx-6 my-0 border-t" />
            {!generationInProgress ? (
              <div className="flex justify-between pt-5 pb-0">
                <Button
                  variant="secondary"
                  disabled={!imagesLoaded && !imageGenerationError}
                  onClick={() => {
                    resetCharacterPickerState();
                  }}
                >
                  Start over
                </Button>
                <Button
                  variant="primary"
                  data-dd-action-name={DATADOG_ACTIONS.GENERATE_CHARACTER}
                  disabled={!imagesLoaded}
                  onClick={handleContinueClick}
                >
                  Continue
                </Button>
              </div>
            ) : (
              // When generation is in progress, show a disabled "Continue" button and a "Cancel" button (in case generation gets stuck)
              <div className="flex justify-between pt-5 pb-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    resetCharacterPickerState();
                  }}
                >
                  Cancel
                </Button>

                <Button variant="secondary" size="sm" disabled>
                  Continue
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Slot>
  );
}

function Percentage({ value }: { value: number | undefined }) {
  if (!value) return <span className="inline-block w-12 text-left"></span>;
  return <span className="inline-block w-12 text-left">{value}%</span>;
}
